import React from "react";
import { ResponsePagerPageSize } from "./PagerPageSize";
import { ResponsePagerControls } from "./PagerControls";
import { Grid, makeStyles } from "@material-ui/core";
import { QueryParams } from "@ofqual/portal-core/core/types";
import { PAGE_SIZE_PARAM_NAME } from "@ofqual/portal-core/core/hooks";
const DEFAULT_PAGE_SIZE = 25;
const PAGE_SIZES = [25, 50, 100];
const DEFAULT_PAGE_LABEL = "Showing page";

const useStyles = makeStyles({
  root: {
    display: "flex",
    alignItems: "center",
  },
  recordsCount: {
    minWidth: "3em",
    display: "inline-block",
    textAlign: "right",
  },
  pageCounts: {
    minWidth: "2em",
    display: "inline-block",
    textAlign: "center",
  },
});

type PagerProps = {
  state: {
    hasData: boolean;
    response: { page?: number; pageSize?: number; count?: number };
  };
  defaultPageSize?: number;
  pageSizes?: number[];
  navigateToNextPage: (newParams: QueryParams) => void;
  showRecordCount?: boolean;
  showPageXofY?: boolean;
  showRowsPerPage?: boolean;
  pageLabel?: string;
  showFirstLastButtons?: boolean;
};

export const Pager: React.FC<PagerProps> = ({
  state: { response, hasData },
  navigateToNextPage,
  defaultPageSize = DEFAULT_PAGE_SIZE,
  pageSizes = PAGE_SIZES,
  showRecordCount = true,
  showPageXofY = true,
  showRowsPerPage = true,
  pageLabel = DEFAULT_PAGE_LABEL,
  showFirstLastButtons = true,
}) => {
  const classes = useStyles();

  const { page = 0, pageSize = -1, count = 0 } = response;
  const pageNumber = page + 1;
  const totalPages = Math.ceil(count / pageSize);

  return (
    <div data-test="pager">
      <Grid container justify="flex-end" spacing={3} className={classes.root}>
        {!hasData ? null : (
          <Grid item>
            &nbsp;
            {/* &nbsp; because whatever happens, we need to have some text present to ensure vertical alignment is always consistent*/}
            {showRecordCount ? (
              <>
                <span className={classes.recordsCount} data-test="page-count">
                  {count}&nbsp;
                </span>
                Records
              </>
            ) : null}
            {!showPageXofY || totalPages === 0 ? null : (
              <>
                &nbsp;&nbsp;&nbsp;{pageLabel}&nbsp;
                <span className={classes.pageCounts} data-test="current-page">
                  {pageNumber}
                </span>
                of
                <span className={classes.pageCounts} data-test="total-pages">{totalPages}</span>
              </>
            )}
            {showRowsPerPage ? (
              <>
                &nbsp; &nbsp; Rows per page:{" "}
                <ResponsePagerPageSize
                  navigate={navigateToNextPage}
                  pageSize={pageSize}
                  paramName={PAGE_SIZE_PARAM_NAME}
                  pageSizes={pageSizes}
                  defaultPageSize={defaultPageSize}
                />
              </>
            ) : null}
          </Grid>
        )}
        <Grid item>
          <ResponsePagerControls
            page={page}
            totalPages={totalPages}
            hasData={hasData}
            navigate={navigateToNextPage}
            showFirstLastButtons={showFirstLastButtons}
          />
        </Grid>
      </Grid>
    </div>
  );
};
